import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CrmAddonPicker, CRM_ADDON_PRICE } from "@/components/CrmAddonPicker";
import { RefundPolicyCheck } from "@/components/RefundPolicyCheck";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD0 = (n) => `$${Number(n).toLocaleString("en-US")}`;

const PLAN_INFO = {
  professional: { label: "Professional", monthly: 2499, annual: 24990 },
  elite: { label: "Elite", monthly: 5998, annual: 59980 },
};

export function TrialCheckoutDialog({ open, onOpenChange, billing, plan = "professional" }) {
  const [form, setForm] = useState({ company: "", name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [crm, setCrm] = useState([]);
  const [agreed, setAgreed] = useState(false);
  useEffect(() => { if (open) { setCrm([]); setAgreed(false); } }, [open, plan, billing]);

  const info = PLAN_INFO[plan] || PLAN_INFO.professional;
  const annual = billing === "annual";
  const lookupKey = `${plan}_${annual ? "annual" : "monthly"}`;
  const price = annual ? info.annual : info.monthly;
  const crmOn = plan === "elite" && crm.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      toast.error("Please fill in your company, name and email.");
      return;
    }
    if (!agreed) { toast.error("Please agree to the Terms of Service and Refund Policy to continue."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, refund_policy_accepted: agreed, terms_accepted: agreed, lookup_key: lookupKey, crm_addon: plan === "elite" ? crm : [], origin_url: window.location.origin, ref_code: localStorage.getItem("deferally_ref") || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Checkout failed");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="trial-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Subscribe to {info.label}</DialogTitle>
          <DialogDescription>
            Full access to DeferAlly {info.label}. Billed today — cancel anytime from your billing settings.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl bg-ally-soft/60 border border-black/5 p-4 flex items-center justify-between">
          <div>
            <div className="font-bold text-ally-ink">{info.label}</div>
            <div className="text-xs text-ally-slate">{annual ? "Billed annually" : "Billed monthly"}</div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl font-black text-ally-ink">{fmtUSD0(price)}</div>
            <div className="text-xs text-ally-slate">{annual ? "/yr" : "/mo"}</div>
          </div>
        </div>

        {plan === "elite" && <CrmAddonPicker value={crm} onChange={setCrm} />}
        {crmOn && (
          <div data-testid="trial-due-today" className="flex items-center justify-between text-sm font-bold text-ally-ink px-1">
            <span>Due today</span><span>{fmtUSD0(price + CRM_ADDON_PRICE)}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3 mt-1">
          <input data-testid="trial-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
            placeholder="Company / firm name"
            className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ally-pink/40" />
          <input data-testid="trial-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your full name"
            className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ally-pink/40" />
          <input data-testid="trial-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Work email"
            className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ally-pink/40" />
          <RefundPolicyCheck checked={agreed} onChange={setAgreed} kind={crmOn ? "planCrm" : "plan"} testid="trial-refund-check" />
          <button data-testid="trial-submit" type="submit" disabled={submitting || !agreed}
            className="w-full rounded-full bg-ally-pink text-white font-bold px-6 py-3.5 hover:bg-ally-pinkHover transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <>Continue to secure checkout <ArrowRight size={18} /></>}
          </button>
          <p className="text-[11px] text-ally-slate flex items-center justify-center gap-1.5">
            <ShieldCheck size={13} /> Secured by Stripe · billed today, cancel anytime
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
